import { router } from "expo-router";
import qs from "qs";

import {
  AUTOMATIC_STRATEGY_NATIVE_SCENE,
  getAutomaticStrategyNativeNavigation
} from "../../lib/automaticStrategySignalsNavigation";
import {
  canOpenNativeStockPageSection,
  isNativeStockPageLegacyScrollSection,
  isNativeStockPageStrategySection
} from "../../lib/StockPage/nativeStockPageStrategySections";
import {
  HOME_NESTED_DETAIL_SEGMENTS,
  STRATEGY_DASHBOARD_PATH_SEGMENTS
} from "../navigation/detailRoutes";
import { TAB_ROUTE_PREFIX } from "./tabRoutePrefix";

/** Scene → path suffix for stock strategy taps (stay on current tab host). */
const AUTOMATIC_STRATEGY_SCENE_PATH_SUFFIX = {
  [AUTOMATIC_STRATEGY_NATIVE_SCENE.SID_SIGNALS]: "sid-dashboard/signals",
  [AUTOMATIC_STRATEGY_NATIVE_SCENE.SUPPLY_DEMAND_SIGNALS]:
    "supply-demand-dashboard/signals",
  [AUTOMATIC_STRATEGY_NATIVE_SCENE.TREND_SIGNALS]: "trend-dashboard/signals",
  [AUTOMATIC_STRATEGY_NATIVE_SCENE.TREND_SUMMARY]: "trend-strategy"
};

export const AUTH_ONBOARDING_ROUTE = "/(auth)";

const SCENE_TO_ROUTE = {
  home: TAB_ROUTE_PREFIX.HOME,
  notifications: TAB_ROUTE_PREFIX.NOTIFICATIONS,
  analysis: TAB_ROUTE_PREFIX.ANALYSIS,
  portfoliosList: TAB_ROUTE_PREFIX.PORTFOLIOS,
  menu: TAB_ROUTE_PREFIX.MENU,
  menuHome: TAB_ROUTE_PREFIX.MENU,
  stocksTable: "/(tabs)/menu/stocks-table",
  priceAlertsTable: "/(tabs)/menu/price-alerts-table",
  commoditiesTable: "/(tabs)/menu/commodities-table",
  cryptocurrenciesTable: "/(tabs)/menu/cryptocurrencies-table",
  currenciesTable: "/(tabs)/menu/currencies-table",
  indicesTable: "/(tabs)/menu/indices-table",
  opsOpdTable: "/(tabs)/menu/ops-opd-table",
  interestingStocks: "/(tabs)/menu/interesting-stocks",
  longTermSignals: "/(tabs)/menu/long-term-signals",
  upcomingFeatures: "/(tabs)/menu/upcoming-features",
  changesHistory: "/(tabs)/menu/changes-history",
  appVersion: "/(tabs)/menu/app-version",
  profileLogin: "/(auth)/login",
  profileRegister: "/(auth)/register",
  resetPassword: "/(auth)/reset-password",
  stockPage: `${TAB_ROUTE_PREFIX.HOME}/stock`,
  loginOtp: "/login-otp",
  searchPage: "/search",
  portfolio: TAB_ROUTE_PREFIX.PORTFOLIOS,
  singlePortfolio: TAB_ROUTE_PREFIX.PORTFOLIOS,
  portfolios: TAB_ROUTE_PREFIX.PORTFOLIOS,
  userSettings: "/(tabs)/menu/settings",
  plans: "/plans",
  fullscreenIntercom: "/intercom",
  insiderAnalysis: `${TAB_ROUTE_PREFIX.ANALYSIS}/insider-analysis`,
  singleAnalysis: `${TAB_ROUTE_PREFIX.ANALYSIS}/single-analysis`,
  submenu: "/(tabs)/menu/submenu",
  turtleStrategySummary: "/turtle-strategy",
  pageNotFound: "/page-not-found",
  sidDashboard: `${TAB_ROUTE_PREFIX.MENU}/sid-dashboard`,
  sidDashboardDescription: `${TAB_ROUTE_PREFIX.MENU}/sid-dashboard/description`,
  sidDashboardTable: `${TAB_ROUTE_PREFIX.MENU}/sid-dashboard/table`,
  sidDashboardSignals: `${TAB_ROUTE_PREFIX.MENU}/sid-dashboard/signals`,
  supplyDemandDashboard: `${TAB_ROUTE_PREFIX.MENU}/supply-demand-dashboard`,
  supplyDemandDashboardDescription: `${TAB_ROUTE_PREFIX.MENU}/supply-demand-dashboard/description`,
  supplyDemandDashboardTable: `${TAB_ROUTE_PREFIX.MENU}/supply-demand-dashboard/table`,
  supplyDemandDashboardSignals: `${TAB_ROUTE_PREFIX.MENU}/supply-demand-dashboard/signals`,
  trendDashboard: `${TAB_ROUTE_PREFIX.MENU}/trend-dashboard`,
  trendDashboardDescription: `${TAB_ROUTE_PREFIX.MENU}/trend-dashboard/description`,
  trendDashboardSignals: `${TAB_ROUTE_PREFIX.MENU}/trend-dashboard/signals`,
  insiderTransactions: "/insider-transactions",
  internalWebBrowser: "/internal-web-browser",
  genericStaticPage: `${TAB_ROUTE_PREFIX.MENU}/generic-static-page`,
  addNewTag: "/tags/new",
  editTag: "/tags",
  addNewOtherAsset: "/other-assets/new",
  editOtherAsset: "/other-assets",
  salesManagoChat: "/salesmanago-chat",
  followedStocksView: "/followed-stocks",
  profileHome: TAB_ROUTE_PREFIX.PORTFOLIOS
};

const TAB_ROOT_ROUTES = new Set(Object.values(TAB_ROUTE_PREFIX));

const NORMALIZED_PATH_TO_SCENE = {
  "/": "home",
  "/notifications": "notifications",
  "/analysis": "analysis",
  "/portfolios": "portfoliosList",
  "/account": "portfoliosList",
  "/menu": "menu",
  "/login": "profileLogin",
  "/register": "profileRegister",
  "/reset-password": "resetPassword",
  "/account/login": "profileLogin",
  "/account/register": "profileRegister",
  "/account/reset-password": "resetPassword",
  "/search": "searchPage",
  "/settings": "userSettings",
  "/menu/settings": "userSettings",
  "/plans": "plans",
  "/intercom": "fullscreenIntercom",
  "/insider-analysis": "insiderAnalysis",
  "/analysis/insider-analysis": "insiderAnalysis",
  "/analysis/single-analysis": "singleAnalysis",
  "/notifications/single-analysis": "singleAnalysis",
  "/portfolios/single-analysis": "singleAnalysis",
  "/menu/single-analysis": "singleAnalysis",
  "/single-analysis": "singleAnalysis",
  "/notifications/insider-analysis": "insiderAnalysis",
  "/portfolios/insider-analysis": "insiderAnalysis",
  "/menu/insider-analysis": "insiderAnalysis",
  "/menu/submenu": "submenu",
  "/turtle-strategy": "turtleStrategySummary",
  "/page-not-found": "pageNotFound",
  "/menu/sid-dashboard": "sidDashboard",
  "/menu/sid-dashboard/description": "sidDashboardDescription",
  "/menu/sid-dashboard/table": "sidDashboardTable",
  "/menu/sid-dashboard/signals": "sidDashboardSignals",
  "/menu/supply-demand-dashboard": "supplyDemandDashboard",
  "/menu/supply-demand-dashboard/description":
    "supplyDemandDashboardDescription",
  "/menu/supply-demand-dashboard/table": "supplyDemandDashboardTable",
  "/menu/supply-demand-dashboard/signals": "supplyDemandDashboardSignals",
  "/menu/trend-dashboard": "trendDashboard",
  "/menu/trend-dashboard/description": "trendDashboardDescription",
  "/menu/trend-dashboard/signals": "trendDashboardSignals",
  "/insider-transactions": "insiderTransactions",
  "/internal-web-browser": "internalWebBrowser",
  "/menu/generic-static-page": "genericStaticPage",
  "/tags/new": "addNewTag",
  "/other-assets/new": "addNewOtherAsset",
  "/salesmanago-chat": "salesManagoChat",
  "/followed-stocks": "followedStocksView"
};

const HOME_STOCK_PATH_REGEX = /^\/stock\/[^/]+\/[^/]+(?:\/section\/[^/]+)?$/;
const TAB_NESTED_STOCK_PATH_REGEX =
  /^\/(?:notifications|analysis|portfolios|menu)\/stock\/[^/]+\/[^/]+(?:\/section\/[^/]+)?$/;
const PORTFOLIO_PATH_REGEX = /^\/(?:account|portfolios)\/[^/]+$/;
const TAG_EDIT_PATH_REGEX = /^\/tags\/[^/]+$/;
const OTHER_ASSET_EDIT_PATH_REGEX = /^\/other-assets\/[^/]+$/;

const AUTH_GROUP_SEGMENT = "(auth)";

const NAVIGATION_GUARD_MS = 500;
let lastNavigation = { signature: null, at: 0 };
let currentRoute = { pathname: "", params: {}, segments: [] };
let lastTabPathname = "/";

export const normalizeNavigationPathname = pathname => {
  if (!pathname) {
    return "/";
  }

  return pathname.replace(/\/\([^/]+\)/g, "") || "/";
};

const matchesPathSegment = (normalized, segment) =>
  normalized === `/${segment}` || normalized.startsWith(`/${segment}/`);

const isHomeNestedDetailPath = normalized =>
  HOME_NESTED_DETAIL_SEGMENTS.some(segment =>
    matchesPathSegment(normalized, segment)
  );

export const getTabRoutePrefixForPathname = pathname => {
  const normalized = normalizeNavigationPathname(pathname);

  if (
    normalized === "/notifications" ||
    normalized.startsWith("/notifications/")
  ) {
    return TAB_ROUTE_PREFIX.NOTIFICATIONS;
  }

  if (normalized === "/analysis" || normalized.startsWith("/analysis/")) {
    return TAB_ROUTE_PREFIX.ANALYSIS;
  }

  if (
    normalized === "/portfolios" ||
    normalized.startsWith("/portfolios/") ||
    normalized === "/account" ||
    normalized.startsWith("/account/")
  ) {
    return TAB_ROUTE_PREFIX.PORTFOLIOS;
  }

  if (normalized === "/menu" || normalized.startsWith("/menu/")) {
    return TAB_ROUTE_PREFIX.MENU;
  }

  if (normalized === "/" || isHomeNestedDetailPath(normalized)) {
    return TAB_ROUTE_PREFIX.HOME;
  }

  return null;
};

/**
 * Current tab stack host for detail screens (all five tabs). Falls back to home.
 */
export const getCurrentHostPrefix = () =>
  getTabRoutePrefixForPathname(currentRoute.pathname) ||
  getTabRoutePrefixForPathname(lastTabPathname) ||
  TAB_ROUTE_PREFIX.HOME;

const getHostPrefixHostingStrategyDashboard = pathname => {
  const normalized = normalizeNavigationPathname(pathname);

  for (const segment of STRATEGY_DASHBOARD_PATH_SEGMENTS) {
    if (normalized.startsWith(`/notifications/${segment}`)) {
      return TAB_ROUTE_PREFIX.NOTIFICATIONS;
    }

    if (normalized.startsWith(`/analysis/${segment}`)) {
      return TAB_ROUTE_PREFIX.ANALYSIS;
    }

    if (normalized.startsWith(`/portfolios/${segment}`)) {
      return TAB_ROUTE_PREFIX.PORTFOLIOS;
    }

    if (normalized.startsWith(`/menu/${segment}`)) {
      return TAB_ROUTE_PREFIX.MENU;
    }

    if (matchesPathSegment(normalized, segment)) {
      return TAB_ROUTE_PREFIX.HOME;
    }
  }

  return null;
};

/**
 * Keep in-dashboard navigation on the stack that currently hosts the dashboard.
 * Menu remains the default for SCENE_TO_ROUTE when not already inside a dashboard.
 */
export const rewriteStrategyDashboardRouteForCurrentHost = route => {
  if (!route || !route.startsWith(TAB_ROUTE_PREFIX.MENU)) {
    return route;
  }

  const relative = route.slice(TAB_ROUTE_PREFIX.MENU.length);
  const segment = relative.replace(/^\//, "").split("/")[0];

  if (!STRATEGY_DASHBOARD_PATH_SEGMENTS.includes(segment)) {
    return route;
  }

  const host =
    getHostPrefixHostingStrategyDashboard(currentRoute.pathname) ||
    getHostPrefixHostingStrategyDashboard(lastTabPathname);

  if (!host || host === TAB_ROUTE_PREFIX.MENU) {
    return route;
  }

  return `${host}${relative}`;
};

const getStockScreenPathname = (marketId, stockId) =>
  `${getCurrentHostPrefix()}/stock/${marketId}/${stockId}`;

const getStockSectionScreenPathname = (marketId, stockId, sectionId) =>
  `${getStockScreenPathname(marketId, stockId)}/section/${encodeURIComponent(
    sectionId
  )}`;

const isStockPath = pathname => {
  const normalized = normalizeNavigationPathname(pathname);

  return (
    HOME_STOCK_PATH_REGEX.test(normalized) ||
    TAB_NESTED_STOCK_PATH_REGEX.test(normalized)
  );
};

export const syncCurrentRoute = (pathname, params = {}, segments = []) => {
  currentRoute = {
    pathname,
    params,
    segments: Array.isArray(segments) ? segments : []
  };

  if (getTabRoutePrefixForPathname(pathname)) {
    lastTabPathname = pathname;
  }
};

export const getSyncedRoute = () => currentRoute;

export const isOnAuthOnboarding = () => {
  const segments = currentRoute.segments ?? [];

  if (segments[0] !== AUTH_GROUP_SEGMENT) {
    return false;
  }

  const screen = segments[1];

  return screen == null || screen === "index";
};

const targetParamsMatchCurrent = (params = {}) => {
  const targetParamKeys = Object.keys(params);

  if (targetParamKeys.length === 0) {
    return true;
  }

  const currentParams = currentRoute.params ?? {};

  return targetParamKeys.every(
    key => JSON.stringify(params[key]) === JSON.stringify(currentParams[key])
  );
};

export const isAlreadyOnRoute = (pathname, params) => {
  const normalizedTarget = normalizeNavigationPathname(pathname);
  const normalizedCurrent = normalizeNavigationPathname(currentRoute.pathname);

  if (normalizedTarget !== normalizedCurrent) {
    return false;
  }

  return targetParamsMatchCurrent(params ?? {});
};

const getNavigationSignature = (pathname, params) =>
  `${pathname}::${JSON.stringify(params ?? {})}`;

const shouldSkipDuplicateNavigation = (pathname, params) => {
  const signature = getNavigationSignature(pathname, params);
  const now = Date.now();

  if (
    lastNavigation.signature === signature &&
    now - lastNavigation.at < NAVIGATION_GUARD_MS
  ) {
    return true;
  }

  lastNavigation = { signature, at: now };
  return false;
};

const shouldSkipNavigation = (pathname, params) =>
  isAlreadyOnRoute(pathname, params) ||
  shouldSkipDuplicateNavigation(pathname, params);

const pushRoute = (pathname, params) => {
  if (params && Object.keys(params).length > 0) {
    router.push({ pathname, params });
  } else {
    router.push(pathname);
  }
};

const guardedPush = (pathname, params) => {
  if (shouldSkipNavigation(pathname, params)) {
    return;
  }

  pushRoute(pathname, params);
};

const guardedNavigate = (pathname, params) => {
  if (shouldSkipNavigation(pathname, params)) {
    return;
  }

  if (params && Object.keys(params).length > 0) {
    router.navigate({ pathname, params });
  } else {
    router.navigate(pathname);
  }
};

export function getSceneNameFromPathname(pathname) {
  if (!pathname) {
    return "";
  }

  const normalizedPathname = pathname.replace(/\/\([^/]+\)/g, "") || "/";

  if (NORMALIZED_PATH_TO_SCENE[normalizedPathname]) {
    return NORMALIZED_PATH_TO_SCENE[normalizedPathname];
  }

  if (isStockPath(normalizedPathname)) {
    return "stockPage";
  }

  if (PORTFOLIO_PATH_REGEX.test(normalizedPathname)) {
    return "portfolio";
  }

  if (TAG_EDIT_PATH_REGEX.test(normalizedPathname)) {
    return "editTag";
  }

  if (OTHER_ASSET_EDIT_PATH_REGEX.test(normalizedPathname)) {
    return "editOtherAsset";
  }

  return "";
}

/**
 * Push a detail screen onto the current tab stack (home / notifications /
 * analysis / portfolios / menu). Path suffix is relative to the tab root
 * (e.g. `single-analysis`, `media-monitor/123`, `sid-dashboard/signals`).
 */
export function navigateToCurrentHostDetail(pathSuffix, params = {}) {
  const suffix = pathSuffix.startsWith("/") ? pathSuffix : `/${pathSuffix}`;
  const pathname = `${getCurrentHostPrefix()}${suffix}`;

  guardedPush(
    pathname,
    params && Object.keys(params).length > 0 ? params : undefined
  );
}

/**
 * Pushes the stock section page onto the current tab's stock stack.
 */
export function navigateToStockPageSection({
  marketId,
  stockId,
  sectionId,
  stockName,
  backTitle
} = {}) {
  if (!marketId || !stockId || !sectionId) {
    console.warn(
      "navigateToStockPageSection requires marketId, stockId, sectionId"
    );
    return;
  }

  if (isNativeStockPageStrategySection(sectionId)) {
    const strategyNavigation = getAutomaticStrategyNativeNavigation({
      notificationsUniqueId: sectionId,
      ticker: stockId,
      market: marketId
    });
    const pathSuffix =
      strategyNavigation &&
      AUTOMATIC_STRATEGY_SCENE_PATH_SUFFIX[strategyNavigation.scene];

    if (pathSuffix) {
      navigateToCurrentHostDetail(pathSuffix, strategyNavigation.params);
      return;
    }
  }

  if (!canOpenNativeStockPageSection(sectionId)) {
    navigateToScene("stockPage", {
      match: {
        params: {
          marketId,
          stockId,
          ...(stockName ? { stockName } : {}),
          ...(backTitle ? { backTitle } : {}),
          ...(isNativeStockPageLegacyScrollSection(sectionId)
            ? { scrollToSection: sectionId }
            : {})
        }
      }
    });
    return;
  }

  const pathname = getStockSectionScreenPathname(marketId, stockId, sectionId);
  const sectionParams = {
    ...(stockName ? { stockName } : {}),
    ...(backTitle ? { backTitle } : {})
  };

  guardedPush(
    pathname,
    Object.keys(sectionParams).length ? sectionParams : undefined
  );
}

export function navigateToScene(sceneName, params = {}) {
  const route = rewriteStrategyDashboardRouteForCurrentHost(
    SCENE_TO_ROUTE[sceneName]
  );

  if (!route) {
    console.warn(`Unknown scene: ${sceneName}`);
    return;
  }

  if (sceneName === "stockPage") {
    const matchParams = params?.match?.params || params;
    const {
      marketId,
      stockId,
      scrollToSection,
      stockName,
      backTitle
    } = matchParams;

    if (scrollToSection && canOpenNativeStockPageSection(scrollToSection)) {
      navigateToStockPageSection({
        marketId,
        stockId,
        sectionId: scrollToSection,
        stockName,
        backTitle
      });
      return;
    }

    const pathname = getStockScreenPathname(marketId, stockId);
    const stockParams = {
      ...(stockName ? { stockName } : {}),
      ...(backTitle ? { backTitle } : {}),
      ...(isNativeStockPageLegacyScrollSection(scrollToSection)
        ? { scrollToSection }
        : {})
    };

    guardedPush(
      pathname,
      Object.keys(stockParams).length ? stockParams : undefined
    );
    return;
  }

  if (sceneName === "singleAnalysis") {
    const matchParams = params?.match?.params || params;
    const {
      analysisId,
      isInsiderAnalysis,
      createPriceAlert,
      stockId,
      price,
      comment
    } = matchParams;
    const analysisParams = {
      ...(analysisId != null ? { analysisId: String(analysisId) } : {}),
      ...(isInsiderAnalysis === true ||
      isInsiderAnalysis === "true" ||
      isInsiderAnalysis === 1 ||
      isInsiderAnalysis === "1"
        ? { isInsiderAnalysis: "true" }
        : {}),
      ...(createPriceAlert != null
        ? { createPriceAlert: String(createPriceAlert) }
        : {}),
      ...(stockId != null ? { stockId: String(stockId) } : {}),
      ...(price != null ? { price: String(price) } : {}),
      ...(comment != null ? { comment: String(comment) } : {})
    };

    navigateToCurrentHostDetail("single-analysis", analysisParams);
    return;
  }

  if (sceneName === "insiderAnalysis") {
    const matchParams = params?.match?.params || params;
    const { categoryId, ticker, market, stockName } = matchParams;
    const insiderParams = {
      ...(categoryId != null ? { categoryId: String(categoryId) } : {}),
      ...(ticker != null ? { ticker: String(ticker) } : {}),
      ...(market != null ? { market: String(market) } : {}),
      ...(stockName != null && stockName !== ""
        ? { stockName: String(stockName) }
        : {})
    };

    navigateToCurrentHostDetail("insider-analysis", insiderParams);
    return;
  }

  if (sceneName === "portfolio" || sceneName === "singlePortfolio") {
    const matchParams = params?.match?.params || {};
    const portfolioId =
      params?.portfolioId ||
      params?.id ||
      matchParams.portfolioId ||
      matchParams.id;
    const portfolioName = params?.portfolioName;
    const pathname = `${TAB_ROUTE_PREFIX.PORTFOLIOS}/${portfolioId}`;
    const portfolioParams = portfolioName ? { portfolioName } : undefined;

    guardedPush(pathname, portfolioParams);
    return;
  }

  if (sceneName === "editTag") {
    const id = params?.id;
    guardedPush(`/tags/${id}`);
    return;
  }

  if (sceneName === "editOtherAsset") {
    const id = params?.id ?? params?.otherAsset?.id;
    guardedPush(`/other-assets/${id}`);
    return;
  }

  // Tab roots must use navigate (not replace) so cross-tab jumps from nested
  // stacks (e.g. menu/submenu) switch the active tab instead of no-op'ing.
  if (TAB_ROOT_ROUTES.has(route)) {
    guardedNavigate(route, params);
    return;
  }

  guardedPush(route, params);
}

/**
 * Flatten a query string into expo-router style route params
 * (`filters[market][0]` keys) so navigation guards match `useGlobalSearchParams`.
 *
 * @param {string} [query=""]
 * @returns {Record<string, string | string[]>}
 */
export function queryStringToFlatRouteParams(query = "") {
  if (!query) {
    return {};
  }

  const params = {};
  const searchParams = new URLSearchParams(
    query.startsWith("?") ? query.slice(1) : query
  );

  searchParams.forEach((value, key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const existing = params[key];
      params[key] = Array.isArray(existing)
        ? [...existing, value]
        : [existing, value];
    } else {
      params[key] = value;
    }
  });

  return params;
}

export function navigateToSceneWithQuery(sceneName, queryOrSettings = {}) {
  const route = rewriteStrategyDashboardRouteForCurrentHost(
    SCENE_TO_ROUTE[sceneName]
  );

  if (!route) {
    console.warn(`Unknown scene: ${sceneName}`);
    return;
  }

  const query =
    typeof queryOrSettings === "string"
      ? queryOrSettings.replace(/^\?/, "")
      : qs.stringify(queryOrSettings);
  const href = query ? `${route}?${query}` : route;
  const params = queryStringToFlatRouteParams(query);

  if (shouldSkipNavigation(route, params)) {
    return;
  }

  router.push(href);
}

export function resetToAuthOnboarding() {
  if (isOnAuthOnboarding()) {
    return;
  }

  router.replace(AUTH_ONBOARDING_ROUTE);
}

export function resetToHome() {
  router.replace(TAB_ROUTE_PREFIX.HOME);
}

export function goBack() {
  if (router.canGoBack()) {
    router.back();
  }
}

export function goBackWithHomeFallback() {
  if (router.canGoBack()) {
    router.back();
    return;
  }

  resetToHome();
}

export function popToTop() {
  router.dismissAll();
}

export { SCENE_TO_ROUTE, TAB_ROUTE_PREFIX };
